/* eslint-disable no-unused-vars */
import React from 'react';
import {
  Archive,
  BookOpen,
  Calendar,
  Chat,
  Clock,
  Footprints,
  MapPin,
  Megaphone,
  Palette,
  Sparkle,
  Star,
  Users
} from '@phosphor-icons/react';

const TYPE_CONFIG = {
  walk:       { icon: Footprints,    color: '#4ade80', bg: 'rgba(74,222,128,0.12)', label: 'Walking Group' },
  discussion: { icon: Chat, color: '#60a5fa', bg: 'rgba(96,165,250,0.12)', label: 'Discussion' },
  event:      { icon: Calendar,      color: '#fbbf24', bg: 'rgba(251,191,36,0.12)', label: 'Event Group' },
  interest:   { icon: Star,          color: '#a78bfa', bg: 'rgba(167,139,250,0.12)', label: 'Interest' },
  creative:   { icon: Palette,       color: '#f472b6', bg: 'rgba(244,114,182,0.12)', label: 'Creative' },
  action:     { icon: Megaphone,     color: '#fb923c', bg: 'rgba(251,146,60,0.12)', label: 'Action' },
};

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return `${Math.floor(days / 7)}w ago`;
}

export default function GroupCard({ group, onClick, isMember, isArchived }) {
  const config = TYPE_CONFIG[group.type] || TYPE_CONFIG.interest;
  const TypeIcon = config.icon;
  const memberCount = group.memberCount || group.members?.length || 0;

  return (
    <div
      onClick={onClick}
      role="button"
      tabIndex={0}
      aria-label={`${group.name} — ${config.label} with ${memberCount} members`}
      onKeyDown={e => e.key === 'Enter' && onClick?.()}
      className="fade-in"
      style={{
        background: '#09090b', border: '1px solid #27272a', borderRadius: 14,
        padding: '14px 16px', cursor: 'pointer', transition: 'all 0.2s',
        opacity: isArchived ? 0.6 : 1,
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = '#3f3f46'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = '#27272a'; e.currentTarget.style.transform = 'none'; }}
    >
      {/* Type badge + status */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 5,
          background: config.bg, padding: '4px 10px', borderRadius: 20,
        }}>
          <TypeIcon size={12} color={config.color} aria-hidden="true" />
          <span style={{ color: config.color, fontSize: 11, fontWeight: 600 }}>{config.label}</span>
        </div>
        {isMember && (
          <span style={{
            background: 'rgba(45,212,191,0.15)', color: '#2dd4bf',
            fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 12,
          }}>Joined</span>
        )}
        {isArchived && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 3,
            background: 'rgba(161,161,170,0.15)', color: '#a1a1aa',
            fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 12,
          }}>
            <Archive size={10} aria-hidden="true" /> Archived
          </span>
        )}
      </div>

      {/* Name */}
      <h3 style={{ color: '#fafafa', fontSize: 15, fontWeight: 700, margin: '0 0 6px', lineHeight: 1.3 }}>
        {group.name}
      </h3>

      {/* Description */}
      {group.description && (
        <p style={{ color: '#a1a1aa', fontSize: 12, margin: '0 0 10px', lineHeight: 1.4,
          overflow: 'hidden', textOverflow: 'ellipsis',
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
        }}>
          {group.description}
        </p>
      )}

      {/* Interests tags */}
      {group.interests?.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 10 }}>
          {group.interests.slice(0, 4).map(tag => (
            <span key={tag} style={{
              background: 'rgba(255,255,255,0.06)', color: '#d4d4d8',
              fontSize: 10, padding: '3px 8px', borderRadius: 10,
            }}>{tag}</span>
          ))}
          {group.interests.length > 4 && (
            <span style={{ color: '#71717a', fontSize: 10, padding: '3px 4px' }}>+{group.interests.length - 4}</span>
          )}
        </div>
      )}

      {/* Footer: members, location, activity */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#71717a', fontSize: 11 }}>
          <Users size={11} aria-hidden="true" /> {memberCount} {memberCount === 1 ? 'member' : 'members'}
        </span>
        {group.location?.suburb && (
          <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#71717a', fontSize: 11 }}>
            <MapPin size={11} aria-hidden="true" /> {group.location.suburb}
          </span>
        )}
        {group.lastActivityAt && (
          <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#71717a', fontSize: 11, marginLeft: 'auto' }}>
            <Clock size={11} aria-hidden="true" /> {timeAgo(group.lastActivityAt)}
          </span>
        )}
      </div>
    </div>
  );
}

export { TYPE_CONFIG };
